/**
 * Pure copy + derived values for the mobile new-appointment sheet, the
 * rebook screen and the booking toast (README screens 5, 6, 8; addendum §4).
 * No React. Rows are salon wall-clock; calendar events carry salon
 * wall-clock as browser-local Dates (see grid/geometry).
 */
import moment from "moment-timezone";
import { CalendarEventType } from "../../../../../types/calendar";
import { formatCurrency } from "../../../../../utils/cashDrawerUtils";
import { ServiceRow } from "../../booking/bookingTypes";
import { serviceLength, toMinutes } from "../../booking/draftModel";
import { eventRangeText, findStaffConflict } from "../detail/detailModel";
import { fmtMin, toMobileAppt } from "../grid/geometry";

/** Design duration selects (prototype `baseOpts` / `addOpts`). */
export const BASE_DURATION_OPTIONS = [15, 30, 45, 60, 75, 90, 120];
export const EXTRA_DURATION_OPTIONS = [0, 5, 10, 15, 20, 30, 45];

/** Options for a duration select; an off-list current value is kept. */
export const durationOptions = (
  values: number[],
  current: number,
  noneLabel?: string,
): { value: string; label: string }[] => {
  const all = values.includes(current)
    ? values
    : [...values, current].sort((a, b) => a - b);
  return all.map((v) => ({
    value: String(v),
    label: v === 0 && noneLabel ? noneLabel : `${v} min`,
  }));
};

/** "Mon, Aug 24" for a "YYYY-MM-DD" day (prototype `dayFull`). */
export const shortDayLabel = (day: string): string =>
  moment(day, "YYYY-MM-DD").format("ddd, MMM D");

/** "Aug 24" (prototype rebook footer). */
export const monthDayLabel = (day: string): string =>
  moment(day, "YYYY-MM-DD").format("MMM D");

/** "h:mm A" for "HH:mm". */
export const clockLabel = (hhmm: string): string => fmtMin(toMinutes(hhmm));

/** "SM" style initials from a full name ("—" when empty). */
export const nameInitials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("") || "—";

/** The provider a row books with. */
export const rowStaffId = (r: ServiceRow): string => r.employeeId || "";

/**
 * The calendar event a row would double-book (same provider, overlapping
 * interval on the booking day), or null.
 */
export const rowConflict = (
  events: CalendarEventType[],
  r: ServiceRow,
  excludeTransactionId?: string,
): CalendarEventType | null => {
  const staffId = rowStaffId(r);
  if (!r.salonServiceId || !r.time || !r.date || !staffId) return null;
  const startMin = toMinutes(r.time);
  return findStaffConflict(events, {
    staffId,
    day: r.date,
    startMin,
    endMin: startMin + serviceLength(r),
    excludeTransactionId,
  });
};

/**
 * Per-card conflict row: "Overlaps Emma Robinson · 10:30 AM – 12:00 PM"; a
 * multi-day time block shows both ends (`eventRangeText`).
 */
export const conflictText = (event: CalendarEventType, now: Date): string =>
  `Overlaps ${toMobileAppt(event, now).client} · ${eventRangeText(event)}`;

/** Double-booking bar copy, naming the provider. */
export const clashBarText = (clashNames: string[]): string =>
  clashNames.length === 1
    ? `That time double books ${clashNames[0] || "the provider"}.`
    : `${clashNames.length} services double book their provider.`;

/** "N services · " prefix, only for more than one service. */
const countPrefix = (n: number): string => (n > 1 ? `${n} services · ` : "");

/** Totals over the chosen rows (minutes exclude buffer, as the design). */
export const draftTotals = (
  rows: ServiceRow[],
  priceOf: (r: ServiceRow) => number,
): { minutes: number; price: number } => ({
  minutes: rows.reduce((n, r) => n + serviceLength(r), 0),
  price: rows.reduce((n, r) => n + priceOf(r), 0),
});

/**
 * Draft footer hint: the summary once bookable, else the first missing
 * piece (service → provider → time on the first incomplete card, then the
 * client).
 */
export const draftFootHint = (
  rows: ServiceRow[],
  hasClient: boolean,
  bookable: boolean,
  totals: { minutes: number; price: number },
): string => {
  if (bookable) {
    return `${countPrefix(rows.length)}${totals.minutes} min · ${formatCurrency(totals.price)}`;
  }
  // The client comes first (services are hidden until one is chosen).
  if (!hasClient) return "Add a client to continue";
  const incomplete = rows.find(
    (r) => !(r.salonServiceId && r.employeeId && r.time),
  );
  if (incomplete) {
    if (!incomplete.salonServiceId) return "Pick a service to continue";
    if (!incomplete.employeeId) return "Pick a provider to continue";
    return "Pick a time to continue";
  }
  return "";
};

/** Review client sub-line: "2 services · Mon, Aug 24 · 90 min". */
export const reviewSubText = (
  count: number,
  day: string,
  minutes: number,
): string =>
  `${count} ${count === 1 ? "service" : "services"} · ${shortDayLabel(day)} · ${minutes} min`;

/** Review WHEN: "Mon, Aug 24 · 10:00 AM – 11:00 AM". */
export const reviewWhenText = (r: ServiceRow): string => {
  if (!r.time || !r.date) return "Not scheduled";
  const start = toMinutes(r.time);
  return `${shortDayLabel(r.date)} · ${fmtMin(start)} – ${fmtMin(start + serviceLength(r))}`;
};

/** Review DURATION: "60 min", "60 min + 15 min buffer". */
export const reviewDurationText = (r: ServiceRow): string =>
  `${serviceLength(r)} min${r.bufferTime ? ` + ${r.bufferTime} min buffer` : ""}`;

export interface ToastCopy {
  title: string;
  /** Second line; omitted for one-line confirmations. */
  note?: string;
}

/** Header Link glyph: the booking link is on the clipboard. */
export const LINK_COPIED_TOAST: ToastCopy = { title: "Booking link copied" };

/**
 * Booking toast (addendum §4): title names the object and the scope, the
 * note lists only what is true.
 */
export const bookingToastCopy = (
  count: number,
  clientName: string,
  totals: { minutes: number; price: number },
): ToastCopy => ({
  title: `${count} ${count === 1 ? "service" : "services"} booked for ${clientName}`,
  note: `${totals.minutes} min · ${formatCurrency(totals.price)}`,
});

/** Rebook quick dates: "In 1 week · Mon, Aug 31" … 4 weeks from the source. */
export const rebookQuickDates = (
  sourceDay: string,
): { day: string; label: string }[] =>
  [1, 2, 3, 4].map((k) => {
    const day = moment(sourceDay, "YYYY-MM-DD")
      .add(k * 7, "days")
      .format("YYYY-MM-DD");
    return {
      day,
      label: `In ${k} ${k === 1 ? "week" : "weeks"} · ${shortDayLabel(day)}`,
    };
  });

/** "YYYY-MM-DD" + n days. */
export const addDaysToDay = (day: string, n: number): string =>
  moment(day, "YYYY-MM-DD").add(n, "days").format("YYYY-MM-DD");
